import { Card, CardContent } from '@/components/ui/card';

interface DilutionPreviewCardProps {
  preMoney: number;
  raiseAmount: number;
  optionPoolIncrease: number;
  currency: string;
}

export function DilutionPreviewCard({
  preMoney,
  raiseAmount,
  optionPoolIncrease,
  currency,
}: DilutionPreviewCardProps) {
  if (!preMoney || !raiseAmount) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-sm text-muted-foreground">
          Complete previous steps to see dilution preview.
        </CardContent>
      </Card>
    );
  }

  const postMoney = preMoney + raiseAmount;
  const investorPct = (raiseAmount / postMoney) * 100;
  const poolPct = optionPoolIncrease * 100;
  const founderPct = 100 - investorPct - poolPct;

  const fmt = (n: number) =>
    n >= 1_000_000
      ? `${currency === 'USD' ? '$' : currency} ${(n / 1_000_000).toFixed(1)}M`
      : `${currency === 'USD' ? '$' : currency} ${(n / 1_000).toFixed(0)}K`;

  const rows = [
    { label: 'Post-money valuation', value: fmt(postMoney), emphasis: true },
    {
      label: 'Founder ownership (post-round)',
      value: `${founderPct.toFixed(1)}%`,
      emphasis: false,
    },
    { label: 'New investor ownership', value: `${investorPct.toFixed(1)}%`, emphasis: false },
    ...(poolPct > 0
      ? [{ label: 'Option pool', value: `${poolPct.toFixed(1)}%`, emphasis: false }]
      : []),
  ];

  return (
    <div className="space-y-4">
      <Card className="overflow-hidden">
        <CardContent className="p-0">
          {/* Visual bar */}
          <div className="flex h-3 overflow-hidden">
            <div className="bg-primary" style={{ width: `${founderPct}%` }} />
            <div className="bg-primary/40" style={{ width: `${investorPct}%` }} />
            {poolPct > 0 && (
              <div className="bg-muted-foreground/20" style={{ width: `${poolPct}%` }} />
            )}
          </div>
          <div className="p-6 space-y-3">
            {rows.map((r) => (
              <div key={r.label} className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{r.label}</span>
                <span className={`tabular-nums font-medium ${r.emphasis ? 'text-lg' : 'text-sm'}`}>
                  {r.value}
                </span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="flex gap-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm bg-primary inline-block" /> Founders
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm bg-primary/40 inline-block" /> New investors
        </span>
        {poolPct > 0 && (
          <span className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm bg-muted-foreground/20 inline-block" /> Option
            pool
          </span>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        This is how ownership changes if you proceed with this round.
      </p>
    </div>
  );
}
